import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Pencil, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { dictionary, Language } from "@/utils/dictionary";
import { useAuth } from "@/context/AuthContext";

interface EditPanelProps {
  onSubmit: (instruction: string) => void;
  onCancel: () => void;
  isLoading: boolean;
}

interface Suggestion {
  labelKey: string;
  templateKey: string;
}

const SUGGESTIONS: Suggestion[] = [
  { labelKey: "change-text", templateKey: "change-text-to" },
  { labelKey: "change-price", templateKey: "change-price-to" },
  { labelKey: "add-price", templateKey: "add-price-to" },
  { labelKey: "free-shipping", templateKey: "add-free-shipping-text" },
  { labelKey: "darker-background", templateKey: "make-background-darker" },
];

export function EditPanel({ onSubmit, onCancel, isLoading }: EditPanelProps) {
  const [instruction, setInstruction] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { language } = useAuth();
  const lang = language as Language;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="overflow-hidden"
    >
      <div className="p-4 bg-muted/30 border-t border-border/40">
        <p className="text-sm font-semibold text-foreground mb-2">
          {dictionary["what-do-you-want-to-change"]?.[lang] || "What do you want to change?"}
        </p>

        <Textarea
          ref={textareaRef}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder={dictionary["describe-what-to-change"]?.[lang] || "Describe what you want to change..."}
          rows={2}
          className="resize-none bg-background border-border/60 focus:ring-primary text-sm mb-2 min-h-[56px]"
          disabled={isLoading}
          autoFocus
        />

        {/* Clickable suggestions */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTIONS.map((s) => {
            const label = dictionary[s.labelKey]?.[lang] || s.labelKey;
            const template = dictionary[s.templateKey]?.[lang] || s.templateKey;
            return (
              <button
                key={s.labelKey}
                type="button"
                onClick={() => {
                  setInstruction(template);
                  requestAnimationFrame(() => {
                    const el = textareaRef.current;
                    if (el) {
                      el.focus();
                      el.setSelectionRange(template.length, template.length);
                    }
                  });
                }}
                disabled={isLoading}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-full border transition-colors duration-150",
                  instruction === template
                    ? "border-primary/40 bg-primary/5 text-primary"
                    : "border-border/50 text-muted-foreground hover:bg-muted hover:text-foreground",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isLoading}
            className="gap-1.5"
          >
            <X className="h-3.5 w-3.5" />
            {dictionary["cancel"]?.[lang] || "Cancel"}
          </Button>
          <Button
            size="sm"
            onClick={() => onSubmit(instruction)}
            disabled={!instruction.trim() || isLoading}
            className="gap-1.5"
          >
            <Pencil className={cn("h-3.5 w-3.5", isLoading && "animate-pulse")} />
            {isLoading
              ? (dictionary["editing"]?.[lang] || "Editing...")
              : (dictionary["edit"]?.[lang] || "Edit")}
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
